import { useMemo, useState, useCallback, useEffect } from 'react';
import { InnovachatRepository } from '../repositories/innovachatRepository';
import { AiArtifact, AuthorType, ChatDynamicParams, Message } from '../types/message';
import { Result } from '@/types/repository/common/result';
import { ChatResponse, ToolCall } from '../types/chatResponse';
import { Conversation, extractConversationTitle, extractLastMessagePreview } from '../types/conversation';
import { uuidv7 } from 'uuidv7';

type Props = {
  jwt?: string;
  initialMessages?: Message[];
  llmConfigId: number | string;
  backendUrl?: string;
  collections?: (number | string)[];
  apiIds?: (number | string)[];
  contextPrompt?: string;
  dynamicParams?: ChatDynamicParams;
  authInstanceId: number | string;
};

const STORAGE_KEY_PREFIX = 'innovachat_';

const useInnovachat = ({
  initialMessages = [],
  jwt,
  llmConfigId,
  collections,
  apiIds,
  contextPrompt,
  dynamicParams,
  backendUrl,
  authInstanceId,
}: Props): {
  messages: Message[];
  artifacts: AiArtifact;
  errorMessage: string | null;
  loading: boolean;
  pendingToolApprovals: Array<ToolCall>;
  reviewApprovals: (approvals: { [toolCallId: string]: boolean }) => void;
  sendMessage: (message: Message) => void;
  clearConversation: () => void;
  // Conversation management
  conversations: Conversation[];
  currentConversationId: string | null;
  createNewConversation: (firstMessage?: string, messageToSend?: Message) => Promise<string>;
  selectConversation: (conversationId: string) => void;
  deleteConversation: (conversationId: string) => void;
} => {
  const innovachatRepository = useMemo(() => {
    const url = backendUrl || import.meta.env.VITE_CHAT_URL;
    return new InnovachatRepository(url);
  }, [backendUrl]);

  const storageKey = useMemo(() => `${STORAGE_KEY_PREFIX}${llmConfigId}_${authInstanceId}`, [llmConfigId, authInstanceId]);

  // Load conversations from localStorage
  const [conversations, setConversations] = useState<Conversation[]>(() => {
    const saved = localStorage.getItem(`${storageKey}_conversations`);
    if (saved) {
      return JSON.parse(saved);
    }
    // Migration: check if there's an old single conversation to migrate
    const oldMessages = localStorage.getItem(`${storageKey}_messages`);
    const oldSessionId = localStorage.getItem(`${storageKey}_sessionId`);
    const oldArtifacts = localStorage.getItem(`${storageKey}_artifacts`);

    if (oldMessages) {
      const messages = JSON.parse(oldMessages);
      if (messages.length > 0) {
        const conversation: Conversation = {
          id: uuidv7(),
          title: extractConversationTitle(messages),
          lastMessagePreview: extractLastMessagePreview(messages),
          timestamp: Date.now(),
          messages,
          sessionId: oldSessionId || undefined,
          artifacts: oldArtifacts ? JSON.parse(oldArtifacts) : {},
        };
        // Clean old storage
        localStorage.removeItem(`${storageKey}_messages`);
        localStorage.removeItem(`${storageKey}_sessionId`);
        localStorage.removeItem(`${storageKey}_artifacts`);
        return [conversation];
      }
    }
    return [];
  });

  const [currentConversationId, setCurrentConversationId] = useState<string | null>(() => {
    const saved = localStorage.getItem(`${storageKey}_currentConversationId`);
    if (saved) return saved;
    // If we migrated an old conversation, set it as current
    const savedConvos = localStorage.getItem(`${storageKey}_conversations`);
    if (!savedConvos) {
      const oldMessages = localStorage.getItem(`${storageKey}_messages`);
      if (oldMessages) {
        const messages = JSON.parse(oldMessages);
        if (messages.length > 0) {
          // The migration happened above, we need to get the ID
          return null; // Will be set after migration runs
        }
      }
    }
    return null;
  });

  // Get current conversation data
  const currentConversation = useMemo(() => {
    return conversations.find(c => c.id === currentConversationId) || null;
  }, [conversations, currentConversationId]);

  const messages = currentConversation?.messages || initialMessages;
  const sessionId = currentConversation?.sessionId;
  const artifacts = currentConversation?.artifacts || {};

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [pendingToolApprovals, setPendingToolApprovals] = useState<Array<ToolCall>>([]);

  // Persist conversations to localStorage
  useEffect(() => {
    localStorage.setItem(`${storageKey}_conversations`, JSON.stringify(conversations));
  }, [conversations, storageKey]);

  // Persist current conversation ID
  useEffect(() => {
    if (currentConversationId) {
      localStorage.setItem(`${storageKey}_currentConversationId`, currentConversationId);
    } else {
      localStorage.removeItem(`${storageKey}_currentConversationId`);
    }
  }, [currentConversationId, storageKey]);

  // Update conversation in the list
  const updateCurrentConversation = useCallback((updates: Partial<Omit<Conversation, 'id'>>) => {
    if (!currentConversationId) return;

    setConversations(prev => prev.map(conv => {
      if (conv.id === currentConversationId) {
        const updated = { ...conv, ...updates };
        // Update derived fields
        if (updates.messages) {
          updated.title = extractConversationTitle(updates.messages);
          updated.lastMessagePreview = extractLastMessagePreview(updates.messages);
          updated.timestamp = Date.now();
        }
        return updated;
      }
      return conv;
    }));
  }, [currentConversationId]);

  const manageChatResponse = useCallback((result: Result<ChatResponse>, targetId: string) => {
    setLoading(false);
    if (!result.data) {
      setErrorMessage(result.error.message || 'Error desconocido');
      return;
    }
    setPendingToolApprovals(result.data?.deferredToolRequests?.approvals || []);

    // Update the conversation with response
    setConversations(prev => prev.map(conv => {
      if (conv.id === targetId) {
        const newMessages = [...conv.messages, result.data.message];
        return {
          ...conv,
          messages: newMessages,
          sessionId: result.data.sessionId,
          artifacts: { ...conv.artifacts, ...result.data.artifacts },
          title: extractConversationTitle(newMessages),
          lastMessagePreview: extractLastMessagePreview(newMessages),
          timestamp: Date.now(),
        };
      }
      return conv;
    }));
  }, []);

  const createNewConversation = useCallback(async (firstMessage?: string, messageToSend?: Message): Promise<string> => {
    const initialMessages: Message[] = firstMessage && messageToSend
      ? [messageToSend]
      : firstMessage
        ? [{ authorType: AuthorType.USER, content: firstMessage }]
        : [];

    const newConversation: Conversation = {
      id: uuidv7(),
      title: firstMessage ? (firstMessage.length > 50 ? firstMessage.substring(0, 47) + '...' : firstMessage) : 'Nueva conversación',
      lastMessagePreview: firstMessage ? `Tú: ${firstMessage.substring(0, 37)}...` : '',
      timestamp: Date.now(),
      messages: initialMessages,
      sessionId: undefined,
      artifacts: {},
    };

    setConversations(prev => [newConversation, ...prev]);
    setCurrentConversationId(newConversation.id);
    setErrorMessage(null);
    setPendingToolApprovals([]);

    // If a message is provided, send it to the backend
    if (messageToSend) {
      setLoading(true);
      try {
        const result = await innovachatRepository.chat({
          jwt,
          llmConfigId,
          collections,
          apiIds,
          sessionId: undefined,
          message: messageToSend,
          context: contextPrompt,
          dynamicParams: dynamicParams,
          authInstanceId,
        });

        manageChatResponse(result, newConversation.id);
      } catch (err) {
        setErrorMessage(err instanceof Error ? err.message : 'An unknown error occurred');
      } finally {
        setLoading(false);
      }
    }

    return newConversation.id;
  }, [innovachatRepository, jwt, llmConfigId, collections, apiIds, contextPrompt, dynamicParams, authInstanceId]);

  const selectConversation = useCallback((conversationId: string) => {
    setCurrentConversationId(conversationId);
    setErrorMessage(null);
    setPendingToolApprovals([]);
  }, []);

  const deleteConversation = useCallback((conversationId: string) => {
    setConversations(prev => prev.filter(c => c.id !== conversationId));
    if (currentConversationId === conversationId) {
      setCurrentConversationId(null);
    }
  }, [currentConversationId]);

  const clearConversation = useCallback(() => {
    if (currentConversationId) {
      deleteConversation(currentConversationId);
    }
  }, [currentConversationId, deleteConversation]);

  const reviewApprovals = useCallback(async (approvals: { [toolCallId: string]: boolean }) => {
    setLoading(true);
    setErrorMessage(null);
    const result = await innovachatRepository.chat({
      jwt,
      llmConfigId,
      collections,
      apiIds,
      sessionId,
      message: undefined,
      context: contextPrompt,
      dynamicParams: dynamicParams,
      authInstanceId,
      deferredToolResults: {
        approvals,
      },
    });

    if (!currentConversationId) {
      setLoading(false);
      setErrorMessage('No active conversation');
      return;
    }

    manageChatResponse(result, currentConversationId);
  }, [innovachatRepository, jwt, llmConfigId, collections, apiIds, sessionId, contextPrompt, dynamicParams, authInstanceId, manageChatResponse, currentConversationId]);

  const sendMessage = useCallback(
    async (message: Message): Promise<void> => {
      if (!currentConversationId) {
        throw new Error('No active conversation. Use createNewConversation first.');
      }

      setLoading(true);
      setErrorMessage(null);

      // Add user message to conversation
      setConversations(prev => prev.map(conv => {
        if (conv.id === currentConversationId) {
          const newMessages = [...conv.messages, message];
          return {
            ...conv,
            messages: newMessages,
            title: extractConversationTitle(newMessages),
            lastMessagePreview: extractLastMessagePreview(newMessages),
            timestamp: Date.now(),
          };
        }
        return conv;
      }));

      try {
        const currentSession = conversations.find(c => c.id === currentConversationId)?.sessionId;

        const result = await innovachatRepository.chat({
          jwt,
          llmConfigId,
          collections,
          apiIds,
          sessionId: currentSession,
          message,
          context: contextPrompt,
          dynamicParams: dynamicParams,
          authInstanceId,
        });

        manageChatResponse(result, currentConversationId);
      } catch (err) {
        setErrorMessage(err instanceof Error ? err.message : 'An unknown error occurred');
        setLoading(false);
      }
    },
    [
      currentConversationId,
      conversations,
      innovachatRepository,
      jwt,
      llmConfigId,
      collections,
      apiIds,
      contextPrompt,
      dynamicParams,
      authInstanceId,
      manageChatResponse,
    ]
  );

  return {
    messages,
    loading,
    errorMessage,
    sendMessage,
    artifacts,
    pendingToolApprovals,
    reviewApprovals,
    clearConversation,
    // Conversation management
    conversations,
    currentConversationId,
    createNewConversation,
    selectConversation,
    deleteConversation,
  };
};

export default useInnovachat;
